/**
 * Shared field rendering for the dynamic onboarding flow.
 *
 * - `ChipInput` is the multi-select-with-custom-values widget extracted verbatim
 *   from the seller/buyer onboarding pages (both used to define it locally).
 * - `FlowFieldRenderer` renders a single admin-configured FieldConfig for a
 *   "dynamic" step (text / number / select / multiselect / checkbox).
 * - `isDynamicStepValid` enforces required-field validation for a dynamic step.
 */

import { KeyboardEvent, useEffect, useState, useRef } from "react";
import type { FieldConfig } from "./service";
import { onboardingFlowPublicService } from "./service";
import PhoneInputField from "../../components/ui/PhoneInputField";
import RequestNumberChange from "../users/RequestNumberChange";
import { MultiOfficeInputField } from "./MultiOfficeInputField";
import InfoTip from "../../components/ui/InfoTip";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { isWebsiteValid } from "../../utils/websiteValidator";

/** The "website" field is admin-configured as a plain "text" field type, but
 * needs URL-specific format validation — keyed off field.key rather than a
 * dedicated field.type since onboarding field types are DB-driven. */
function isWebsiteField(field: FieldConfig): boolean {
  return field.key === "website";
}

// ── Phone field split-key helpers ───────────────────────────────────────────
// Phone-type fields store TWO sibling keys in a step's data instead of one
// combined E.164 string: `{field.key}_country_code` (e.g. "+91") and
// `{field.key}` (national number only, e.g. "9812345678"). The UI still
// renders a single PhoneInputField per phone field.

/** The sibling key holding a phone field's country calling code. */
export function phoneCountryCodeKey(fieldKey: string): string {
  return `${fieldKey}_country_code`;
}

/** True for the account-level WhatsApp/OTP number field (mirrors the backend's
 * `_is_locked_phone_key` in auth/onboarding.py) — business numbers such as
 * `business_mobile_number` are excluded and stay fully editable.
 *
 * This number is verified once at signup and is the number every login OTP is
 * sent to, so it is never self-service editable: the backend rejects any save
 * that would move it with HTTP 409 `ACCOUNT_NUMBER_LOCKED` (users/service.py
 * `_raise_account_number_locked`), and it renders read-only here — in the
 * onboarding wizard and the profile editor alike, both of which go through
 * this renderer. */
function isAccountWhatsappKey(fieldKey: string): boolean {
  if (fieldKey.includes("business")) return false;
  return fieldKey === "mobile_number" || /(_mobile_number|whatsapp_number)$/i.test(fieldKey);
}

/** Combine split country-code + national-number values into an E.164 string. */
export function combinePhoneE164(countryCode: FlowFieldValue | undefined, number: FlowFieldValue | undefined): string {
  const cc = typeof countryCode === "string" ? countryCode.trim() : "";
  const num = typeof number === "string" ? number.trim() : "";
  if (!num) return "";
  return num.startsWith("+") ? num : `${cc}${num}`;
}

/** Split an E.164 phone string into { countryCode, number } (national number, no leading +). */
export function splitPhoneE164(e164: string): { countryCode: string; number: string } {
  if (!e164) return { countryCode: "", number: "" };
  try {
    const parsed = parsePhoneNumber(e164);
    if (parsed) {
      return { countryCode: `+${parsed.countryCallingCode}`, number: parsed.nationalNumber };
    }
  } catch {
    /* fall through to best-effort below */
  }
  return { countryCode: "", number: e164.replace(/^\+/, "") };
}

// ── Currency field split-key helpers ────────────────────────────────────────
// Currency-number fields store TWO sibling keys, same pattern as phone:
// `{field.key}_currency` (e.g. "USD") and `{field.key}` (the numeric amount).

/** The sibling key holding a currency-number field's selected currency code. */
export function currencyCodeKey(fieldKey: string): string {
  return `${fieldKey}_currency`;
}

/** Tiny bootstrap fallback if GET /api/v1/onboarding/options/currencies is
 * empty/unreachable — the admin-curated "currencies" onboarding option set
 * (same source of truth used by MyInventoryPage.tsx and the backend's
 * get_allowed_currencies) is the real list. */
const CURRENCY_FALLBACK: { value: string; label: string }[] = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "INR", label: "INR" },
  { value: "AED", label: "AED" },
  { value: "SGD", label: "SGD" },
];

// ── LabeledOption (shared shape used by ChipInput / selects) ────────────────

export interface LabeledOption {
  value: string;
  label: string;
  description?: string;
}

// ── ChipInput — predefined options + custom free text ──────────────────────
// Format is enforced server-side only (backend/app/modules/users/service.py);
// this component no longer blocks input on the client.

export interface ChipInputProps {
  label: string;
  sublabel?: string;
  options?: LabeledOption[];
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  required?: boolean;
  loading?: boolean;
  searchable?: boolean;
  cardStyle?: boolean;
  infoTip?: string;
}

export function ChipInput({
  label,
  sublabel,
  options = [],
  values,
  onChange,
  placeholder = "Type and press Enter to add…",
  required,
  loading,
  searchable = false,
  cardStyle = false,
  infoTip,
}: ChipInputProps) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");

  const ciIncludes = (arr: string[], val: string) =>
    arr.some((x) => x.toLowerCase() === val.toLowerCase());

  /** Always returns true (accepted); format is enforced server-side. */
  const add = (val: string): boolean => {
    const v = val.trim();
    if (!v || ciIncludes(values, v)) return true;
    onChange([...values, v]);
    return true;
  };
  const remove = (v: string) =>
    onChange(values.filter((x) => x.toLowerCase() !== v.toLowerCase()));
  /** Removes an option's stored entry whether it's saved as its value or its
   *  legacy label (e.g. a pre-migration "Hotel Chain" instead of "hotel_chain"). */
  const removeOption = (opt: LabeledOption) =>
    onChange(values.filter((x) => x.toLowerCase() !== opt.value.toLowerCase() && x.toLowerCase() !== opt.label.toLowerCase()));
  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); if (add(input)) setInput(""); }
  };

  const filtered = searchable && search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1">
        <span className="text-sm font-medium text-ink">
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        {infoTip && <InfoTip message={infoTip} />}
        {sublabel && <span className="text-xs text-ink-muted">({sublabel})</span>}
      </div>

      {loading ? (
        <div className="flex h-10 items-center gap-2">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
          <span className="text-xs text-ink-muted">Loading…</span>
        </div>
      ) : options.length > 0 && (
        <div className="mb-3">
          {searchable && (
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…" className="input mb-2 w-full text-sm" />
          )}
          {cardStyle ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {filtered.map((opt) => {
                // Legacy-stored entries may carry the old display label instead
                // of the canonical value (e.g. "Hotel Chain" vs "hotel_chain"),
                // so match against either before deciding the card is selected.
                const active = ciIncludes(values, opt.value) || ciIncludes(values, opt.label);
                return (
                  <button key={opt.value} type="button"
                    onClick={() => active ? removeOption(opt) : add(opt.value)}
                    className={`rounded-xl border p-3 text-left transition-all ${
                      active
                        ? "border-brand bg-brand/10 ring-2 ring-brand"
                        : "border-border bg-surface hover:border-brand/40 hover:bg-brand/5"
                    }`}
                  >
                    <p className={`text-sm font-semibold ${active ? "text-brand" : "text-ink"}`}>{opt.label}</p>
                    {opt.description && <p className="mt-0.5 text-xs leading-snug text-ink-muted">{opt.description}</p>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              <div className="flex flex-wrap gap-2 pb-1">
                {filtered.map((opt) => {
                  const active = ciIncludes(values, opt.value) || ciIncludes(values, opt.label);
                  return (
                    <button key={opt.value} type="button" title={opt.description}
                      onClick={() => active ? removeOption(opt) : add(opt.value)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                        active
                          ? "border-brand bg-brand text-white"
                          : "border-border bg-surface text-ink hover:border-brand/50 hover:bg-brand/5"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey} placeholder={placeholder} className="input input-lg flex-1 text-sm" />
        <button type="button" onClick={() => { if (add(input)) setInput(""); }} disabled={!input.trim()}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-surface-subtle disabled:opacity-40">
          Add
        </button>
      </div>

      {values.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-3 py-1 text-xs font-medium text-brand">
              {v}
              <button type="button" onClick={() => remove(v)} className="ml-0.5 text-brand/60 hover:text-brand">✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Dynamic field value model ───────────────────────────────────────────────

/** A dynamic field's value: string for text/select, number, string[] for multiselect, boolean for checkbox, any[] for offices, object for group. */
export type FlowFieldValue = string | number | boolean | string[] | any[] | Record<string, any>;

/** Sensible empty default for a field given its type. */
export function defaultFieldValue(field: FieldConfig): FlowFieldValue {
  switch (field.type) {
    case "multiselect": return [];
    case "checkbox":    return false;
    case "yesno":       return ""; // "" = unanswered, then "yes" | "no"
    case "number":      return "";
    case "currency_number": return "";
    case "offices":     return [{ country: "", city: "" }];
    case "multiple":    return [];
    case "gst":         return [];
    case "group":       return {};
    default:            return "";
  }
}

/** True if v is a plain (non-array) object. */
function isPlainObject(v: unknown): v is Record<string, any> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Coerces a multiselect/chip array to safely-renderable strings. Some legacy
 *  onboarding records stored a raw object (e.g. a stray `{City: ..., ...}`
 *  map) as an array entry instead of a string, which crashes React when
 *  rendered as a JSX child — so any non-string entry is stringified here. */
function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => {
    if (typeof v === "string") return v;
    if (v === null || v === undefined) return "";
    if (isPlainObject(v)) return Object.keys(v).join(", ");
    return String(v);
  }).filter((v) => v !== "");
}

// Basic email shape: something@something.tld
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when a field has no value entered yet (presence only, ignores format). */
export function isFieldEmpty(field: FieldConfig, value: FlowFieldValue): boolean {
  switch (field.type) {
    case "multiselect":
    case "offices":
    case "multiple":
    case "gst":       return !Array.isArray(value) || value.length === 0;
    case "group": {
      if (!isPlainObject(value)) return true;
      const subFields = field.sub_fields || [];
      const groupValue = value as Record<string, any>;
      return subFields.every((sf) => isFieldEmpty(sf, groupValue[sf.key] ?? defaultFieldValue(sf)));
    }
    case "checkbox":  return value !== true;
    case "yesno":     return value !== "yes" && value !== "no";
    case "number":
    case "currency_number": return value === "" || value === null || value === undefined;
    default:          return typeof value !== "string" || value.trim().length === 0;
  }
}

/**
 * Format-level validity for a PRESENT value (date/email/phone/number).
 * Presence is checked separately via isFieldEmpty; types with no format
 * constraint (text/select/upload/…) are always format-valid.
 */
export function isFieldFormatValid(field: FieldConfig, value: FlowFieldValue): boolean {
  if (isWebsiteField(field)) {
    return typeof value === "string" && isWebsiteValid(value);
  }
  switch (field.type) {
    case "email":  return typeof value === "string" && EMAIL_RE.test(value.trim());
    case "phone":  return typeof value === "string" && value.replace(/\D/g, "").length >= 8;
    case "date":   return typeof value === "string" && !Number.isNaN(Date.parse(value));
    case "number":
    case "currency_number": return !Number.isNaN(Number(value));
    default:       return true;
  }
}

/** True when a single required field's value is considered satisfied (present AND valid). */
export function isFieldSatisfied(field: FieldConfig, value: FlowFieldValue): boolean {
  if (isWebsiteField(field)) {
    return typeof value === "string" && value.trim().length > 0 && isWebsiteValid(value);
  }
  switch (field.type) {
    case "multiselect": return Array.isArray(value) && value.length > 0;
    case "checkbox":    return value === true; // required+checkbox ⇒ must be checked
    case "yesno":       return value === "yes" || value === "no"; // required ⇒ must pick one
    case "email":       return typeof value === "string" && EMAIL_RE.test(value.trim());
    case "phone":       return typeof value === "string" && value.replace(/\D/g, "").length >= 8;
    case "date":        return typeof value === "string" && !Number.isNaN(Date.parse(value));
    case "number":
    case "currency_number": return value !== "" && !Number.isNaN(Number(value));
    case "offices":     return Array.isArray(value) && value.length > 0 && value.some((v) => v.country && v.city);
    case "gst":         return Array.isArray(value) && value.length > 0 && value.every((v) =>
      typeof v?.tax_number === "string" && v.tax_number.trim().length > 0
    );
    case "group": {
      if (!isPlainObject(value)) return false;
      const subFields = field.sub_fields || [];
      const groupValue = value as Record<string, any>;
      return subFields
        .filter((sf) => sf.required)
        .every((sf) => isFieldSatisfied(sf, groupValue[sf.key] ?? defaultFieldValue(sf)));
    }
    case "multiple": {
      if (!Array.isArray(value) || value.length === 0) return false;
      return value.every(item => {
        if (!item || typeof item !== "object") return false;
        const keys = Object.keys(item);
        if (keys.length === 0) return false;
        const activeKey = keys[0];
        const activeSubField = field.sub_fields?.find(sf => sf.key === activeKey);
        if (!activeSubField) return false;
        return isFieldSatisfied(activeSubField, item[activeKey]);
      });
    }
    default:            return typeof value === "string" && value.trim().length > 0;
  }
}

/** True when a phone field's split country-code + number are both present and form a valid E.164 number. */
export function isPhoneFieldSatisfied(fieldKey: string, values: Record<string, FlowFieldValue>): boolean {
  const cc = values[phoneCountryCodeKey(fieldKey)];
  const num = values[fieldKey];
  const ccStr = typeof cc === "string" ? cc.trim() : "";
  const numStr = typeof num === "string" ? num.trim() : "";
  if (!ccStr || !numStr) return false;
  const e164 = combinePhoneE164(ccStr, numStr);
  return isValidPhoneNumber(e164);
}

/** True when a phone field has neither a country code nor a number entered. */
export function isPhoneFieldEmpty(fieldKey: string, values: Record<string, FlowFieldValue>): boolean {
  const cc = values[phoneCountryCodeKey(fieldKey)];
  const num = values[fieldKey];
  const ccStr = typeof cc === "string" ? cc.trim() : "";
  const numStr = typeof num === "string" ? num.trim() : "";
  return !ccStr && !numStr;
}

/** True when a currency-number field's currency and amount are both present and the amount is numeric. */
export function isCurrencyFieldSatisfied(fieldKey: string, values: Record<string, FlowFieldValue>): boolean {
  const currency = values[currencyCodeKey(fieldKey)];
  const amount = values[fieldKey];
  const currencyStr = typeof currency === "string" ? currency.trim() : "";
  if (!currencyStr) return false;
  return amount !== "" && amount !== null && amount !== undefined && !Number.isNaN(Number(amount));
}

/** True when a currency-number field has neither a currency nor an amount entered. */
export function isCurrencyFieldEmpty(fieldKey: string, values: Record<string, FlowFieldValue>): boolean {
  const currency = values[currencyCodeKey(fieldKey)];
  const amount = values[fieldKey];
  const currencyStr = typeof currency === "string" ? currency.trim() : "";
  const amountEmpty = amount === "" || amount === null || amount === undefined;
  return !currencyStr && amountEmpty;
}

/** True when every required field in a dynamic step has a non-empty value. */
export function isDynamicStepValid(
  fields: FieldConfig[],
  values: Record<string, FlowFieldValue>,
): boolean {
  return fields.every((f) => {
    if (f.type === "phone") {
      if (f.required) return isPhoneFieldSatisfied(f.key, values);
      if (isPhoneFieldEmpty(f.key, values)) return true;
      return isPhoneFieldSatisfied(f.key, values);
    }
    if (f.type === "currency_number") {
      if (f.required) return isCurrencyFieldSatisfied(f.key, values);
      if (isCurrencyFieldEmpty(f.key, values)) return true;
      return isCurrencyFieldSatisfied(f.key, values);
    }
    const v = values[f.key] ?? defaultFieldValue(f);
    if (f.required) return isFieldSatisfied(f, v);
    // Optional field: fine if left empty, but if filled it must be well-formed
    // (e.g. a stray invalid email/phone should still block progress).
    if (isFieldEmpty(f, v)) return true;
    return isFieldFormatValid(f, v);
  });
}

// ── Options loading hook (one fetch per options_type, cached) ───────────────

const _optionsCache = new Map<string, LabeledOption[]>();

function normalizeOptions(items: Awaited<ReturnType<typeof onboardingFlowPublicService.getOptions>>): LabeledOption[] {
  return items.map((it) =>
    typeof it === "string"
      ? { value: it, label: it }
      : { value: it.value, label: it.label, description: it.description },
  );
}

function useFieldOptions(optionsType?: string | null, stepFields?: FieldConfig[], stepValues?: Record<string, FlowFieldValue>) {
  const [options, setOptions] = useState<LabeledOption[]>(() =>
    optionsType ? _optionsCache.get(optionsType) ?? [] : [],
  );
  const [loading, setLoading] = useState<boolean>(() =>
    Boolean(optionsType) && !_optionsCache.has(optionsType as string),
  );

  // Derive selected countries and states
  const countryField = stepFields?.find(f => f.options_type === "countries");
  const cValRaw = countryField ? stepValues?.[countryField.key] : null;
  const selectedCountries = Array.isArray(cValRaw) ? cValRaw : (typeof cValRaw === "string" && cValRaw ? [cValRaw] : []);

  const stateField = stepFields?.find(f => f.options_type === "states");
  const sValRaw = stateField ? stepValues?.[stateField.key] : null;
  const selectedStates = Array.isArray(sValRaw) ? sValRaw : (typeof sValRaw === "string" && sValRaw ? [sValRaw] : []);

  const depKey = `${optionsType}|${[...selectedCountries].sort().join(",")}|${[...selectedStates].sort().join(",")}`;

  useEffect(() => {
    if (!optionsType) { setOptions([]); setLoading(false); return; }
    
    // For states and cities, if we don't have a country, we can't fetch anything.
    if ((optionsType === "states" || optionsType === "cities") && selectedCountries.length === 0) {
      setOptions([]); setLoading(false); return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchGeo = async () => {
      const { geoService } = await import("../geo/service");
      if (optionsType === "countries") {
        const data = await geoService.getCountries();
        return data.map(c => ({ value: c.name, label: c.name }));
      }
      
      if (optionsType === "states" && selectedCountries.length > 0) {
        const allCountries = await geoService.getCountries();
        const resolvedIso2s = selectedCountries.map(nameOrIso => 
          allCountries.find(c => c.name === nameOrIso || c.iso2 === nameOrIso)?.iso2
        ).filter(Boolean) as string[];

        if (resolvedIso2s.length === 0) return [];
        
        const statePromises = resolvedIso2s.map(iso2 => geoService.getStates(iso2));
        const allStates = (await Promise.all(statePromises)).flat();
        
        return allStates.map(s => ({ value: s.name, label: s.name }));
      }
      
      if (optionsType === "cities" && selectedCountries.length > 0) {
        const allCountries = await geoService.getCountries();
        const resolvedIso2s = selectedCountries.map(nameOrIso => 
          allCountries.find(c => c.name === nameOrIso || c.iso2 === nameOrIso)?.iso2
        ).filter(Boolean) as string[];

        if (resolvedIso2s.length === 0) return [];

        let allCities: { name: string }[] = [];

        for (const cIso2 of resolvedIso2s) {
          const countryStates = await geoService.getStates(cIso2);
          
          let targetStateIso2s: string[] = [];
          if (selectedStates.length > 0) {
             const matchedStates = countryStates.filter(s => selectedStates.includes(s.name) || selectedStates.includes(s.isoCode));
             targetStateIso2s = matchedStates.map(s => s.isoCode);
             
             if (countryStates.length > 0 && targetStateIso2s.length === 0) {
                // Country has states, but none of them are selected, so skip fetching cities for this country.
                continue;
             }
          }
          
          if (targetStateIso2s.length > 0) {
             const cityPromises = targetStateIso2s.map(sIso2 => geoService.getCities(cIso2, sIso2));
             const cityArrays = await Promise.all(cityPromises);
             allCities.push(...cityArrays.flat());
          } else {
             const cities = await geoService.getCities(cIso2);
             allCities.push(...cities);
          }
        }
        
        const uniqueCities = Array.from(new Set(allCities.map(c => c.name)));
        return uniqueCities.map(name => ({ value: name, label: name }));
      }
      
      return onboardingFlowPublicService.getOptions(optionsType).then(normalizeOptions);
    };

    fetchGeo()
      .then((items) => {
        if (optionsType !== "states" && optionsType !== "cities") {
           _optionsCache.set(optionsType, items);
        }
        if (!cancelled) setOptions(items);
      })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [depKey]);

  return { options, loading };
}

// ── SearchableSelect (Single select with search) ─────────────────────────────

export function SearchableSelect({
  value,
  onChange,
  options,
  loading,
  placeholder = "Select…",
}: {
  value: string;
  onChange: (v: string) => void;
  options: LabeledOption[];
  loading?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const displayLabel = options.find((o) => o.value === value)?.label ?? (typeof value === "string" ? value : "");

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={loading}
        onClick={() => { if (!loading) { setOpen((o) => !o); setQuery(""); } }}
        className={`input input-lg w-full flex items-center justify-between text-left ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <span className={displayLabel ? "text-ink" : "text-ink-muted"}>
          {loading ? "Loading…" : displayLabel || placeholder}
        </span>
        <svg className={`w-4 h-4 text-ink-muted flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-border rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="input w-full text-sm"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-ink-muted">No results</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); setQuery(""); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-muted transition-colors
                    ${opt.value === value ? "bg-brand/10 text-brand font-medium" : "text-ink"}`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SearchableMultiSelect (Multi select with search & chips) ─────────────────

export function SearchableMultiSelect({
  value,
  onChange,
  options,
  loading,
  placeholder = "Select…",
  pendingValues,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: LabeledOption[];
  loading?: boolean;
  placeholder?: string;
  /** Values (matched case-insensitively against either the raw value or its
   *  resolved label) awaiting admin verification — rendered amber instead of
   *  the default brand color, with a "pending verification" note. */
  pendingValues?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggleOption = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const removeOption = (val: string) => {
    onChange(value.filter((v) => v !== val));
  };

  const pendingSet = new Set((pendingValues || []).map((p) => p.toLowerCase()));
  const isPending = (val: string, label: string) =>
    pendingSet.has(val.toLowerCase()) || pendingSet.has(label.toLowerCase());

  return (
    <div ref={ref} className="relative w-full">
      {/* Selected Chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map(val => {
            const label = options.find((o) => o.value === val)?.label ?? val;
            const pending = isPending(val, label);
            return (
              <span
                key={val}
                title={pending ? "Awaiting admin verification — deals here can't publish yet" : undefined}
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                  pending ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300" : "bg-brand/10 text-brand"
                }`}
              >
                {label}
                {pending && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" />}
                <button type="button" onClick={() => removeOption(val)} className={`ml-1.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full transition-colors ${pending ? "text-amber-600 hover:bg-amber-200" : "text-brand/60 hover:bg-brand/20 hover:text-brand"}`}>
                  <span className="sr-only">Remove {label}</span>
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown Trigger */}
      <button
        type="button"
        disabled={loading}
        onClick={() => { if (!loading) { setOpen((o) => !o); setQuery(""); } }}
        className={`input w-full flex items-center justify-between text-left min-h-[42px] ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <span className="text-ink-muted">
          {loading ? "Loading…" : placeholder}
        </span>
        <svg className={`w-4 h-4 text-ink-muted flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-border rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="input w-full text-sm"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-ink-muted">No results</div>
            ) : (
              filtered.map((opt) => {
                const active = value.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { toggleOption(opt.value); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-muted transition-colors flex items-center justify-between
                      ${active ? "bg-brand/5 text-brand font-medium" : "text-ink"}`}
                  >
                    <span>{opt.label}</span>
                    {active && (
                      <svg className="w-4 h-4 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FlowFieldRenderer — one configured field of a dynamic step ──────────────

export interface FlowFieldRendererProps {
  field: FieldConfig;
  value: FlowFieldValue;
  onChange: (value: FlowFieldValue) => void;
  stepFields?: FieldConfig[];
  stepValues?: Record<string, FlowFieldValue>;
  /** Used by multi-key field types (e.g. phone) to set a sibling key alongside `field.key`. */
  onChangeKey?: (key: string, value: FlowFieldValue) => void;
  /** Values awaiting admin verification (from onboarding.pending_market_review
   *  + pending_market_review_names). Applies an amber highlight in multiselects. */
  pendingValues?: string[];
}

export function FlowFieldRenderer({ field, value, onChange, stepFields, stepValues, onChangeKey, pendingValues }: FlowFieldRendererProps) {
  const { options, loading } = useFieldOptions(
    field.type === "select" || field.type === "multiselect"
      ? field.options_type
      : field.type === "currency_number"
      ? "currencies"
      : null,
    stepFields,
    stepValues
  );

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Per-row GST verify status for the repeatable "gst" field type — keyed by
  // registration index. Separate from the single legacy `gst_vat_number`
  // field's `isGstVerified` flag below.
  const [gstVerifyState, setGstVerifyState] = useState<
    Record<number, { status: "idle" | "verifying" | "queued" | "verified" | "unavailable" | "error"; message?: string | null }>
  >({});

  // Tracks the latest `value` prop so async handlers (e.g. GST live-verify)
  // can read the current field value at resolution time instead of a
  // stale closure captured when the async call was kicked off.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // job_id -> true while its poll loop should keep running — cleared on
  // unmount so a stale poll never fires a save/state-update against an
  // unmounted field. Mirrors web-frontend's areas/profile/FieldRenderer.tsx
  // pollQueuedJob, which this app's onboarding flow was missing: without it,
  // a GST verify that fell through to the background worker (see
  // verify_or_enqueue's 5s sync cap) never got its result applied here — the
  // UI just permanently showed "an admin will verify this GST number later"
  // even though the worker usually finishes within a few seconds.
  const activeGstPollsRef = useRef<Set<string>>(new Set());
  useEffect(() => () => activeGstPollsRef.current.clear(), []);

  const isGstField = field.key === "gst_vat_number";
  const isGstVerified = isGstField && stepValues?.gst_verified === true;
  const gstAddress = isGstField && (stepValues?.gst_verification as any)?.address;

  const labelRow = (
    <div className="mb-2">
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-ink">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </span>
        {isGstVerified && (
          <span title="Verified by GST Portal" className="text-sm ml-1">✅</span>
        )}
        {field.info_tip && <InfoTip message={field.info_tip} />}
      </div>
      {gstAddress && (
        <div className="mt-1.5 text-xs text-brand-dark bg-brand/10 p-2 rounded-lg border border-brand/20">
          <span className="font-medium">Registered Address:</span> {gstAddress}
        </div>
      )}
    </div>
  );

  // Inline format hint shown only once a value is entered but malformed.
  const formatErrorMsg =
    field.type !== "phone" && !isFieldEmpty(field, value) && !isFieldFormatValid(field, value)
      ? isWebsiteField(field) ? "Enter a valid website URL, e.g. https://example.com."
      : field.type === "email"  ? "Enter a valid email address."
      : field.type === "date"   ? "Enter a valid date."
      : field.type === "number" || field.type === "currency_number" ? "Enter a valid number."
      : null
      : null;
  const formatErrorNode = formatErrorMsg ? (
    <p className="mt-1 text-xs text-red-500">{formatErrorMsg}</p>
  ) : null;

  if (field.type === "multiselect") {
    const isGeo = ["countries", "states", "cities"].includes(field.options_type || "");
    const useDropdown = isGeo || options.length > 20;

    return (
      <div>
        {labelRow}
        {useDropdown ? (
          <SearchableMultiSelect
            value={toStringArray(value)}
            onChange={(v) => onChange(v)}
            options={options}
            loading={loading}
            placeholder={`Select ${field.label.toLowerCase()}…`}
            pendingValues={pendingValues}
          />
        ) : (
          <ChipInput
            label=""
            required={false}
            options={options}
            values={toStringArray(value)}
            onChange={(v) => onChange(v)}
            loading={loading}
            cardStyle={true}
          />
        )}
      </div>
    );
  }

  if (field.type === "group") {
    const groupValue: Record<string, any> = isPlainObject(value) ? value : {};
    const subFields = field.sub_fields || [];

    return (
      <div className="w-full">
        {labelRow}
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-border bg-surface p-4">
          {subFields.map((sf) => (
            <div key={sf.key} className={sf.type === "upload" || sf.type === "textarea" ? "sm:col-span-2" : ""}>
              <FlowFieldRenderer
                field={sf}
                value={groupValue[sf.key] ?? defaultFieldValue(sf)}
                onChange={(v) => onChange({ ...groupValue, [sf.key]: v })}
                stepFields={subFields}
                stepValues={groupValue}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "multiple") {
    // Defensive: legacy/stray records may hold a bare `{key: val}` object
    // instead of the `[{key: val}]` array this field type has always saved —
    // wrap it so old data still renders instead of silently appearing blank.
    const items = Array.isArray(value) && value.length > 0
      ? value
      : (value && typeof value === "object" && !Array.isArray(value) ? [value] : [{}]);
    const subFields = field.sub_fields || [];

    const handleAdd = () => {
      onChange([...items, {}]);
    };

    const handleRemove = (idx: number) => {
      const next = items.filter((_, i) => i !== idx);
      onChange(next.length > 0 ? next : [{}]);
    };

    const handleTypeSelect = (idx: number, subFieldKey: string) => {
      const sf = subFields.find(f => f.key === subFieldKey);
      if (!sf) return;
      const nextItems = [...items];
      nextItems[idx] = { [subFieldKey]: defaultFieldValue(sf) };
      onChange(nextItems);
    };

    const handleItemChange = (idx: number, subFieldKey: string, subVal: FlowFieldValue) => {
      const nextItems = [...items];
      nextItems[idx] = { [subFieldKey]: subVal };
      onChange(nextItems);
    };

    return (
      <div className="w-full">
        {labelRow}
        <div className="space-y-4 mt-2">
          {items.map((item, idx) => {
            const activeKey = Object.keys(item)[0];
            const activeSubField = activeKey ? subFields.find(sf => sf.key === activeKey) : null;

            return (
              <div key={idx} className="p-4 border border-border rounded-xl bg-surface relative group">
                <button 
                  type="button" 
                  onClick={() => handleRemove(idx)}
                  className="absolute top-2 right-2 text-ink-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 z-10"
                >
                  ✕
                </button>

                {!activeSubField ? (
                  <div>
                    <select
                      className="input w-full"
                      value=""
                      onChange={(e) => handleTypeSelect(idx, e.target.value)}
                    >
                      <option value="" disabled>Select field type...</option>
                      {subFields.map(sf => (
                        <option key={sf.key} value={sf.key}>{sf.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-ink-muted uppercase tracking-wider">{activeSubField.label}</span>
                      <button 
                        type="button" 
                        onClick={() => {
                          const nextItems = [...items];
                          nextItems[idx] = {};
                          onChange(nextItems);
                        }}
                        className="text-xs text-brand hover:underline"
                      >
                        Change type
                      </button>
                    </div>
                    <FlowFieldRenderer
                      field={activeSubField}
                      value={item[activeSubField.key] ?? defaultFieldValue(activeSubField)}
                      onChange={(v) => handleItemChange(idx, activeSubField.key, v)}
                      stepFields={subFields}
                      stepValues={item}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <button 
            type="button" 
            onClick={handleAdd}
            className="text-sm font-medium text-brand hover:text-brand/80"
          >
            + Add another {field.label}
          </button>
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        {labelRow}
        <SearchableSelect
          value={typeof value === "string" ? value : ""}
          onChange={(v) => onChange(v)}
          options={options}
          loading={loading}
        />
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink select-none">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded"
        />
        <div className="flex items-center gap-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
          {field.info_tip && <InfoTip message={field.info_tip} />}
        </div>
      </label>
    );
  }

  if (field.type === "yesno") {
    const selected = typeof value === "string" ? value : "";
    return (
      <div>
        {labelRow}
        <div className="inline-flex overflow-hidden rounded-lg border border-border">
          {(["yes", "no"] as const).map((opt) => {
            const active = selected === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                className={`px-5 py-2 text-sm font-medium capitalize transition-colors ${
                  active
                    ? "bg-brand text-white"
                    : "bg-surface text-ink hover:bg-surface-subtle"
                } ${opt === "yes" ? "border-r border-border" : ""}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div>
        {labelRow}
        <input
          type="number"
          min={0}
          value={typeof value === "number" || typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : String(Math.max(0, Number(e.target.value))))}
          className="input input-lg w-full"
        />
        {formatErrorNode}
      </div>
    );
  }

  if (field.type === "currency_number") {
    const currencyValue = stepValues?.[currencyCodeKey(field.key)];
    const currency = typeof currencyValue === "string" && currencyValue ? currencyValue : "USD";
    const currencyOptions = options.length > 0 ? options : CURRENCY_FALLBACK;
    return (
      <div>
        {labelRow}
        <div className="flex gap-2">
          <select
            value={currency}
            onChange={(e) => onChangeKey?.(currencyCodeKey(field.key), e.target.value)}
            className="input input-lg w-32 flex-shrink-0"
            disabled={loading}
          >
            {currencyOptions.map((c) => (
              <option key={c.value} value={c.value}>{c.value}</option>
            ))}
          </select>
          <input
            type="number"
            min={0}
            value={typeof value === "number" || typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : String(Math.max(0, Number(e.target.value))))}
            placeholder="Amount"
            className="input input-lg w-full"
          />
        </div>
        {formatErrorNode}
      </div>
    );
  }

  if (field.type === "phone") {
    const ccValue = stepValues?.[phoneCountryCodeKey(field.key)];
    const combined = combinePhoneE164(
      typeof ccValue === "string" ? ccValue : "",
      typeof value === "string" ? value : "",
    );
    const isAccountNumber = isAccountWhatsappKey(field.key);
    const phoneInput = (
      <PhoneInputField
        label={field.label}
        infoTip={field.info_tip}
        value={combined}
        onChange={(e164) => {
          if (isAccountNumber) return;
          const { countryCode, number } = splitPhoneE164(e164);
          onChange(number);
          onChangeKey?.(phoneCountryCodeKey(field.key), countryCode);
        }}
        required={field.required}
        placeholder={isAccountNumber ? "WhatsApp number" : undefined}
        disabled={isAccountNumber}
        hint={isAccountNumber ? "Verified — an admin has to change it for you." : undefined}
      />
    );
    if (!isAccountNumber) return phoneInput;
    // The field stays read-only, but the old dead-end "email contact@gullivr.ai"
    // hint is now an actual request the user can file from here.
    return (
      <div>
        {phoneInput}
        <RequestNumberChange />
      </div>
    );
  }

  if (field.type === "offices") {
    return (
      <MultiOfficeInputField
        label={field.label}
        infoTip={field.info_tip}
        required={field.required}
        values={Array.isArray(value) ? value : []}
        onChange={(v) => onChange(v)}
        pendingValues={pendingValues}
      />
    );
  }

  if (field.type === "gst") {
    const items: any[] = Array.isArray(value) && value.length > 0 ? value : [{ tax_type: "GST", tax_number: "", company_name: "", brand_name: "", offices: [] }];

    const handleAdd = () => {
      onChange([...items, { tax_type: "GST", tax_number: "", company_name: "", brand_name: "", offices: [] }]);
    };

    const handleRemove = (idx: number) => {
      const next = items.filter((_, i) => i !== idx);
      onChange(next.length > 0 ? next : [{ tax_type: "GST", tax_number: "", company_name: "", brand_name: "", offices: [] }]);
      setGstVerifyState((prev) => {
        const { [idx]: _drop, ...rest } = prev;
        return rest;
      });
    };

    const handleItemField = (idx: number, key: string, val: any) => {
      const next = [...items];
      next[idx] = { ...next[idx], [key]: val };
      onChange(next);
      // Any manual edit to the GSTIN invalidates whatever verify result was
      // showing for this row — force a fresh explicit Verify click.
      if (key === "tax_number") {
        setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "idle" } }));
      }
    };

    const handleTaxTypeChange = (idx: number, nextType: "GST" | "VAT") => {
      const next = [...items];
      // Switching type drops any GST-derived lock/verification state so a
      // stale legal_name can't survive a type switch.
      next[idx] = { ...next[idx], tax_type: nextType, company_name: "" };
      onChange(next);
      setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "idle" } }));
    };

    const applyGstVerifyResult = (idx: number, result: { legal_name?: string | null; office?: any }) => {
      // Re-derive from the latest value at resolution time, not the `items`
      // closure captured when the click fired, so an edit made while this
      // request was in flight isn't clobbered.
      const latestValue = valueRef.current;
      const currentItems: any[] =
        Array.isArray(latestValue) && latestValue.length > 0
          ? latestValue
          : [{ tax_type: "GST", tax_number: "", company_name: "", brand_name: "", offices: [] }];
      if (idx >= currentItems.length) return;
      const next = [...currentItems];
      const parsedOffice = result.office
        ? [{
            country: result.office.country,
            country_code: result.office.country_code,
            state: result.office.state,
            state_code: result.office.state_code,
            city: result.office.city,
            address: result.office.address,
            zip_code: result.office.pincode,
            is_main: true,
          }]
        : (next[idx].offices ?? []);
      // An existing offices[0] that's just an untouched blank placeholder
      // (no city/address/country filled in) shouldn't block a freshly
      // verified office from populating the form.
      const existingOffices = next[idx].offices ?? [];
      const hasRealOfficeData = existingOffices.some(
        (o: any) => (o?.city || "").trim() || (o?.address || "").trim() || (o?.country || "").trim(),
      );
      next[idx] = {
        ...next[idx],
        company_name: next[idx].company_name || result.legal_name,
        offices: result.office && !hasRealOfficeData ? parsedOffice : existingOffices,
      };
      onChange(next);
    };

    // Picks up a GST verify call that fell through to the background worker
    // (verifyGstLive's sync path timed out/503'd within its 5s cap). Polls
    // every 3s until the job resolves, then applies the result exactly like
    // the synchronous path would have. `silent` mirrors handleGstBlur's
    // best-effort semantics — a stuck/failed job from a blur check degrades
    // to the "unavailable" message rather than surfacing a hard error.
    const pollQueuedGstJob = (idx: number, jobId: string) => {
      activeGstPollsRef.current.add(jobId);
      const POLL_INTERVAL_MS = 3000;
      const tick = async () => {
        if (!activeGstPollsRef.current.has(jobId)) return; // cancelled
        try {
          const job = await onboardingFlowPublicService.getGstVerifyJobStatus(jobId);
          if (!activeGstPollsRef.current.has(jobId)) return; // cancelled mid-request
          if (job.status === "completed") {
            activeGstPollsRef.current.delete(jobId);
            if (job.legal_name) {
              applyGstVerifyResult(idx, job);
              setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "verified", message: job.legal_name } }));
            } else {
              // A job reaching "completed" with no legal_name means an admin
              // manually approved it (approve_job has no real portal data to
              // auto-fill from, by design) — gst_verified is already true on
              // the backend, so the message must say so, not read like
              // nothing has happened yet.
              setGstVerifyState((prev) => ({
                ...prev,
                [idx]: {
                  status: "unavailable",
                  message: "GST number verified by our team. Please fill in your company name and office address manually.",
                },
              }));
            }
            return;
          }
          if (job.status === "rejected") {
            // Distinct from failed/stopped_for_review: an admin explicitly
            // reviewed and rejected this GSTIN — the ball is back in the
            // user's court to re-enter it, not something to passively wait
            // on. The backend already blanks the rejected value out of this
            // row's data (see apply_gst_rejection_to_step), so the field
            // itself comes back empty on next load — this message is what
            // explains why.
            activeGstPollsRef.current.delete(jobId);
            setGstVerifyState((prev) => ({
              ...prev,
              [idx]: { status: "error", message: job.error || "This GST number was rejected. Please re-enter and verify it." },
            }));
            return;
          }
          if (job.status === "failed" || job.status === "stopped_for_review") {
            activeGstPollsRef.current.delete(jobId);
            setGstVerifyState((prev) => ({
              ...prev,
              [idx]: { status: "unavailable", message: "Live verification unavailable — an admin will verify this GST number later." },
            }));
            return;
          }
          // Still queued/in_progress — keep polling.
          setTimeout(() => void tick(), POLL_INTERVAL_MS);
        } catch {
          activeGstPollsRef.current.delete(jobId);
          setGstVerifyState((prev) => ({
            ...prev,
            [idx]: { status: "unavailable", message: "Live verification unavailable — an admin will verify this GST number later." },
          }));
        }
      };
      setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    // Explicit "Verify" button per registration row — shows a clear
    // loading/success/error state instead of the silent best-effort blur
    // check below. This is the primary verify path the seller sees.
    const handleGstVerifyClick = async (idx: number) => {
      const gstin = (items[idx]?.tax_number || "").trim();
      if (!gstin) {
        setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "error", message: "Enter a GST number first." } }));
        return;
      }
      setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "verifying" } }));
      try {
        const result = await onboardingFlowPublicService.verifyGstLive(gstin);
        if (result.queued && result.job_id) {
          setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "queued", message: "Verifying in the background…" } }));
          pollQueuedGstJob(idx, result.job_id);
          return;
        }
        if (!result.legal_name) {
          // GST_VERIFICATION_ENABLED is off (or the portal had no match) —
          // the endpoint still resolves 200 with nulled-out fields, so don't
          // paint a false "Verified" checkmark. Still unlock company/office
          // fields so onboarding isn't blocked; an admin verifies separately.
          setGstVerifyState((prev) => ({
            ...prev,
            [idx]: { status: "unavailable", message: "Live verification unavailable — an admin will verify this GST number later." },
          }));
          return;
        }
        applyGstVerifyResult(idx, result);
        setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "verified", message: result.legal_name } }));
      } catch (err: any) {
        setGstVerifyState((prev) => ({
          ...prev,
          [idx]: {
            status: "error",
            message: err?.response?.data?.detail || "Could not verify this GST number. Check it and try again.",
          },
        }));
      }
    };

    const handleGstBlur = async (idx: number, gstin: string) => {
      const trimmed = gstin.trim();
      if (!trimmed) return;
      try {
        const result = await onboardingFlowPublicService.verifyGstLive(trimmed);
        if (result.queued && result.job_id) {
          setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "queued", message: "Verifying in the background…" } }));
          pollQueuedGstJob(idx, result.job_id);
          return;
        }
        if (!result.legal_name) {
          setGstVerifyState((prev) => ({
            ...prev,
            [idx]: { status: "unavailable", message: "Live verification unavailable — an admin will verify this GST number later." },
          }));
          return;
        }
        applyGstVerifyResult(idx, result);
        setGstVerifyState((prev) => ({ ...prev, [idx]: { status: "verified", message: result.legal_name } }));
      } catch {
        // Silent here — the explicit Verify button above is the primary
        // feedback path; blur-verify remains best-effort UX only.
      }
    };

    return (
      <div className="w-full">
        {labelRow}
        <div className="space-y-4 mt-2">
          {items.map((item, idx) => {
            // GST rows keep company/brand/office fields hidden until that row's
            // GST number is verified (either just now via the Verify button, or
            // previously — a returning user's saved row already has these values,
            // so we treat non-empty company_name/offices as "already unlocked"
            // rather than re-locking data that was legitimately captured before).
            const isGstRow = item.tax_type !== "VAT";
            const rowVerified =
              gstVerifyState[idx]?.status === "verified" ||
              gstVerifyState[idx]?.status === "unavailable" ||
              Boolean((item.company_name || "").trim()) ||
              (Array.isArray(item.offices) && item.offices.length > 0);
            const unlocked = !isGstRow || rowVerified;

            // If it's a GST row that wasn't marked 'unavailable', and it has a company name,
            // we treat its auto-populated fields (company_name and first office) as locked.
            const isAutoPopulated =
              isGstRow &&
              (gstVerifyState[idx]?.status === "verified" ||
                (Boolean((item.company_name || "").trim()) &&
                  gstVerifyState[idx]?.status !== "unavailable"));

            return (
            <div key={idx} className="p-4 border border-border rounded-xl bg-surface relative group">
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="absolute top-2 right-2 text-ink-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 z-10"
              >
                ✕
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-ink-muted uppercase tracking-wider">Registration Type</label>
                  <select
                    className="input w-full mt-1"
                    value={item.tax_type || "GST"}
                    onChange={(e) => handleTaxTypeChange(idx, e.target.value as "GST" | "VAT")}
                  >
                    <option value="GST">GST</option>
                    <option value="VAT">VAT</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-ink-muted uppercase tracking-wider">
                    {item.tax_type === "VAT" ? "VAT Number" : "GST Number"}
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      className="input w-full"
                      value={item.tax_number || ""}
                      onChange={(e) => handleItemField(idx, "tax_number", e.target.value)}
                      onBlur={(e) => {
                        if (item.tax_type === "GST") handleGstBlur(idx, e.target.value);
                      }}
                    />
                    {item.tax_type === "GST" && (
                      <button
                        type="button"
                        onClick={() => handleGstVerifyClick(idx)}
                        disabled={
                          gstVerifyState[idx]?.status === "verifying" ||
                          gstVerifyState[idx]?.status === "queued" ||
                          !(item.tax_number || "").trim()
                        }
                        className="shrink-0 px-3 py-2 text-xs font-medium rounded-lg border border-border bg-surface text-ink hover:bg-brand/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {gstVerifyState[idx]?.status === "verifying" || gstVerifyState[idx]?.status === "queued"
                          ? "Verifying…"
                          : "Verify"}
                      </button>
                    )}
                  </div>
                  {item.tax_type === "GST" && gstVerifyState[idx]?.status === "verified" && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      ✅ Verified{gstVerifyState[idx]?.message ? `: ${gstVerifyState[idx]?.message}` : ""}
                    </p>
                  )}
                  {item.tax_type === "GST" && gstVerifyState[idx]?.status === "queued" && (
                    <p className="mt-1 text-xs text-ink-muted">{gstVerifyState[idx]?.message}</p>
                  )}
                  {item.tax_type === "GST" && gstVerifyState[idx]?.status === "unavailable" && (
                    <p className="mt-1 text-xs text-ink-muted">{gstVerifyState[idx]?.message}</p>
                  )}
                  {item.tax_type === "GST" && gstVerifyState[idx]?.status === "error" && (
                    <p className="mt-1 text-xs text-red-500">{gstVerifyState[idx]?.message}</p>
                  )}
                  {isGstRow && !unlocked && gstVerifyState[idx]?.status !== "error" && (
                    <p className="mt-1 text-xs text-ink-muted">
                      Verify your GST number to add company &amp; office details.
                    </p>
                  )}
                </div>
                {unlocked && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-ink-muted uppercase tracking-wider">Company Name</label>
                      <input
                        type="text"
                        className="input w-full mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        value={item.company_name || ""}
                        onChange={(e) => handleItemField(idx, "company_name", e.target.value)}
                        disabled={isAutoPopulated}
                        title={isAutoPopulated ? "Auto-populated from GST verification and cannot be edited." : undefined}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-ink-muted uppercase tracking-wider">Brand Name</label>
                      <input
                        type="text"
                        className="input w-full mt-1"
                        value={item.brand_name || ""}
                        onChange={(e) => handleItemField(idx, "brand_name", e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
              {unlocked && (
                <div className="mt-4">
                  <MultiOfficeInputField
                    label="Office Locations"
                    values={Array.isArray(item.offices) ? item.offices : []}
                    onChange={(v) => handleItemField(idx, "offices", v)}
                    disabledItemIndex={isAutoPopulated ? 0 : undefined}
                  />
                </div>
              )}
            </div>
            );
          })}
          <button type="button" onClick={handleAdd} className="text-sm font-medium text-brand hover:text-brand/80">
            + Add another registration
          </button>
        </div>
      </div>
    );
  }

  if (field.type === "upload") {
    return (
      <div className="w-full">
        {labelRow}
        <div className="mt-2">
          <input
            type="file"
            disabled={uploading}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) {
                onChange("");
                setUploadedFileName(null);
                return;
              }
              setUploadError(null);
              setUploading(true);
              try {
                const result = await onboardingFlowPublicService.uploadDocument(file);
                onChange(result.key);
                setUploadedFileName(file.name);
              } catch (err: any) {
                setUploadError(err?.response?.data?.detail || "Upload failed. Please try again.");
                onChange("");
                setUploadedFileName(null);
              } finally {
                setUploading(false);
                e.target.value = "";
              }
            }}
            className="block w-full text-sm text-slate-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-brand/10 file:text-brand
              hover:file:bg-brand/20
              border border-border rounded-lg bg-surface px-3 py-2 cursor-pointer
              disabled:opacity-60 disabled:cursor-not-allowed"
          />
        </div>
        {uploading && (
          <p className="mt-2 flex items-center gap-2 text-xs text-ink-muted">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            Uploading…
          </p>
        )}
        {!uploading && uploadError && (
          <p className="mt-2 text-xs text-red-500">{uploadError}</p>
        )}
        {!uploading && !uploadError && typeof value === "string" && value && (
          <p className="mt-2 text-xs text-ink-muted">
            Uploaded: <span className="font-medium text-ink">{uploadedFileName || value.split("/").pop()}</span>
          </p>
        )}
      </div>
    );
  }

  if (field.type === "date") {
    return (
      <div>
        {labelRow}
        <input
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="input input-lg w-full"
        />
        {formatErrorNode}
      </div>
    );
  }

  if (field.type === "email") {
    return (
      <div>
        {labelRow}
        <input
          type="email"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="input input-lg w-full"
        />
        {formatErrorNode}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        {labelRow}
        <textarea
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="input w-full min-h-[120px] resize-y py-3"
        />
        {formatErrorNode}
      </div>
    );
  }

  // "text" falls back here.
  return (
    <div>
      {labelRow}
      <input
        type={isWebsiteField(field) ? "url" : "text"}
        inputMode={isWebsiteField(field) ? "url" : undefined}
        placeholder={isWebsiteField(field) ? "https://example.com" : undefined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="input input-lg w-full"
      />
      {formatErrorNode}
    </div>
  );
}
